/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { PageQueryParams, SearchQueryParams, SortQueryParams } from '../apiCommon.typings';

export interface CashRegistersModelsListFilterParams extends Partial<SearchQueryParams> {
    name?: string;
    status?: CashRegistersModelStatus[];
    operatorTaxNumber?: string[];
    agent?: boolean;
    portable?: boolean;
}

export type CashRegisterModelsListRequestParams = PageQueryParams &
    Partial<SortQueryParams> &
    CashRegistersModelsListFilterParams;
export type CashRegisterModelsExcelRequestParams = CashRegistersModelsListFilterParams;

export interface OperatorCashRegistersListFilterParams {
    name?: string;
    status?: CashRegistersModelStatus;
}

export interface CashRegisterModelsListDto {
    count: number;
    cashRegisterModels: CashRegisterModelDto[];
}

export interface CashRegisterModelDto {
    taxNumber: string;
    name: string;
    status: CashRegistersModelStatus;
    operatorTaxNumber: string;
    count: number;
    registrationDateAtUtc: string;
    attestationDateAtUtc?: string;
    activationDateAtUtc?: string;
    reattestationDateAtUtc?: string;
    eliminationDateAtUtc?: string;
    agent: boolean;
    portable?: boolean;
}

export enum VersionStatus {
    ATTESTATION = 'ATTESTATION',
    ACTIVE = 'ACTIVE',
    OUTDATED = 'OUTDATED',
    ELIMINATED = 'ELIMINATED',
    UNKNOWN = 'UNKNOWN',
}

export class VersionDto {
    id: string;
    name: string;
    status: VersionStatus;
    statusDateAtUtc: string;
    fiscalDriverVersion?: string;
    firmwareVersion?: string;
    checksum?: string;
}

export interface VersionsListDto {
    count: number;
    versions: VersionDto[];
}

export interface CheckSumDto {
    checksum: string;
    fiscalDriverVersion: string;
    firmwareVersion: string;
    insertedDateAtUtc: string;
}

export interface CheckSumsListDto {
    count: number;
    checksums: CheckSumDto[];
}

export class VersionWithCheckSums extends VersionDto {
    checksumHistory?: CheckSumsListDto;
    isExpanded?: boolean;
}

export interface SoftwareVersionsHistory {
    count: number;
    versions: VersionWithCheckSums[];
}

export interface VersionCheckSumsParams {
    versionId: string;
    versionName: string;
    pageParams: PageQueryParams;
}

export const CASH_REGISTER_MODEL_STATUS = [
    'REGISTRATION',
    'ACTIVE',
    'ELIMINATED',
    'ATTESTATION',
    'REATTESTATION',
] as const;

export type CashRegistersModelStatus = (typeof CASH_REGISTER_MODEL_STATUS)[number];

export interface CashRegisterModelDetailsDto {
    name: string;
    taxNumber: string;
    operatorTaxNumber: string;
    operatorName: string;
    softName: string;
    hardwareName: string;
    activities: string[];
    cashRegistersCount: number;
    statusHistory: StatusHistoryDto[];
    attestationKey: string;
    attestationHost: string;
    attestationPort: number;
    agent: boolean;
    portable?: boolean;
}

export interface StatusHistoryDto {
    status: CashRegistersModelStatus;
    statusDateAtUtc: string;
    userName: string;
    comment: string;
    active: boolean;
}

export interface CashRegisterModelsCounts {
    registration: number;
    active: number;
    eliminated: number;
    attestation: number;
    reattestation: number;
}

export interface UpdateModelDetailsParams {
    agent?: boolean;
    portable?: boolean;
}

export interface StartAttestationDto {
    taxNumber: string;
    versionName: string;
    comment: string;
    documents?: FileInfo[];
}

export enum AttestationResult {
    ACCEPT = 'ACCEPT',
    REJECT = 'REJECT',
}

export enum AttestationResponseStatus {
    OK = 'OK',
    SEVEN_DAYS = 'SEVEN_DAYS',
    WRONG_VERSION_NAME = 'WRONG_VERSION_NAME',
    CHECKSUM_CHANGED = 'CHECKSUM_CHANGED',
    FD_VERSION_CHANGED = 'FD_VERSION_CHANGED',
    FW_VERSION_OUTDATED = 'FW_VERSION_OUTDATED',
    FD_VERSION_OUTDATED = 'FD_VERSION_OUTDATED',
}

export interface AttestationResponseDto {
    accepted: boolean;
    responseStatus: AttestationResponseStatus;
    message?: string;
}

export interface CompleteAttestationDto {
    taxNumber: string;
    versionName: string;
    comment: string;
    result: AttestationResult;
    forceAttestation: boolean;
}

export interface FactoryNumbersListDto {
    count: number;
    cashRegisters: FactoryNumberDto[];
}

export interface FactoryNumberDto {
    factoryNumber: string;
    registrationDate?: string;
    cashBoxTaxNumber?: string;
}

export type FactoryNumberTypes = boolean | 'ALL';

export type FactoryNumbersListQueryParams = PageQueryParams & FactoryNumbersListFilterParams;

export interface FactoryNumbersListFilterParams {
    factoryNumber: string;
    used: FactoryNumberTypes;
}

export interface ModelDocumentsListQueryParams {
    limit?: number;
    offset?: number;
    sortType?: string;
    sortOrder?: string;
}

export interface ModelDocumentDto {
    id: string;
    name: string;
    userName: string;
    userId: string;
    insertedAtUtc: string;
    versionName?: string;
    versionId?: string;
}

export interface ModelDocumentsListDto {
    count: number;
    files: ModelDocumentDto[];
}

export interface FileInfo {
    id: string;
    name: string;
    userId: string;
}

export interface PresignedFileInfo extends FileInfo {
    presignedPutObjectUrl: string;
}

export interface SubmitDocumentsDto {
    files: FileInfo[];
    versionId?: string;
}

export type SoftwareVersionStatus = VersionStatus.ACTIVE | VersionStatus.OUTDATED | VersionStatus.UNKNOWN;

export interface SoftwareVersionItemDto {
    id: number;
    name: string;
    status: SoftwareVersionStatus;
    statusDateAtUtc: string;
}
